import { useTimeline } from '@src/features/absence';
import type { VisibleRange } from '@src/features/absence';
import { useFormContext } from '@src/shared/form';
import { formatIsoDate } from '@src/shared/lib';
import { useEffect, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  buildHolidayLookup,
  getAbsencesIdsForRange,
  getUserAbsences,
  userResourceId,
} from '../../../lib';
import type { ScheduleFormValues } from '../Form';
import TotalDatesOrHours from '../TotalDatesOrHours';
import { DatesRangeField } from './Dates';
import HoursToggle from './HoursToggle';
import NotesField from './NotesField';
import TimeRangeField from './Time';
import TypeField from './TypeField';
import UsersField from './User';

type FormFieldsetProps = {
  disabled?: boolean;
  disableUserField?: boolean;
};

const SCROLL_LEAD_DAYS = 7;

function isWithinVisibleRange(date: Date, range: VisibleRange) {
  const t = date.getTime();
  return t >= range.start.getTime() && t < range.end.getTime();
}

function areIdsEqual(left: number[], right: number[]) {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((id, index) => id === right[index]);
}

export default function FormFieldset({
  disabled = false,
  disableUserField = false,
}: FormFieldsetProps) {
  const { setValue, watch } =
    useFormContext() as UseFormReturn<ScheduleFormValues>;
  const {
    absences,
    setSelectedSlot,
    selectedSlotData,
    calendar,
    isDayView,
    setSelectedUserId,
    expandAndScrollToResource,
    holidays,
    users,
    visibleRange,
  } = useTimeline();
  const holidayLookup = useMemo(() => buildHolidayLookup(holidays), [holidays]);
  const userId = watch('userId');
  const selectedUserBranchId = useMemo(
    () => users.find((user) => user.id === userId)?.branch?.id ?? null,
    [userId, users],
  );
  const [dateFrom, dateTo] = watch(['dateFrom', 'dateTo']);
  const [startTime, endTime] = watch(['startTime', 'endTime']);

  useEffect(() => {
    setSelectedSlot((slot) => {
      if (!slot) return null;
      let nextStartTime: string | null = isDayView ? startTime || null : null;
      let nextEndTime: string | null = isDayView ? endTime || null : null;

      if (isDayView && !nextStartTime && !nextEndTime) {
        const editedId = slot.editedAbsenceId ?? slot.editedAbsenceIds?.[0];
        const editedAbsence = editedId
          ? absences.find((a) => a.id === editedId)
          : null;

        if (
          editedAbsence &&
          !editedAbsence.allDay &&
          editedAbsence.startTime != null &&
          editedAbsence.endTime != null
        ) {
          nextStartTime = editedAbsence.startTime;
          nextEndTime = editedAbsence.endTime;
        } else {
          const slotDate = dateFrom ? formatIsoDate(dateFrom) : slot.start;
          const userAbsences = getUserAbsences(slot.userId, absences);
          const firstHourly = userAbsences
            .filter(
              (a) =>
                a.dateStr === slotDate &&
                !a.allDay &&
                a.startTime != null &&
                a.endTime != null,
            )
            .sort((a, b) =>
              (a.startTime ?? '').localeCompare(b.startTime ?? ''),
            )[0];

          if (firstHourly) {
            nextStartTime = firstHourly.startTime;
            nextEndTime = firstHourly.endTime;
          }
        }
      }
      let selectedIds: number[] = [];
      if (dateFrom && dateTo) {
        selectedIds = getAbsencesIdsForRange(
          dateFrom,
          dateTo,
          getUserAbsences(slot?.userId, absences),
          {
            dayView: isDayView,
            startTime: nextStartTime,
            endTime: nextEndTime,
            excludeIds:
              slot.editedAbsenceIds ??
              (slot.editedAbsenceId ? [slot.editedAbsenceId] : []),
          },
        );
      } else if (slot.start && slot.end) {
        selectedIds = getAbsencesIdsForRange(
          new Date(slot.start),
          new Date(slot.end),
          getUserAbsences(slot?.userId, absences),
          {
            dayView: isDayView,
            startTime: nextStartTime,
            endTime: nextEndTime,
            excludeIds:
              slot.editedAbsenceIds ??
              (slot.editedAbsenceId ? [slot.editedAbsenceId] : []),
          },
        );
      }
      const changedSlot = {
        userId,
        resourceId: userResourceId(userId),
        ...(dateFrom ? { start: formatIsoDate(dateFrom) } : {}),
        ...(dateTo ? { end: formatIsoDate(dateTo) } : {}),
        startTime: nextStartTime,
        endTime: nextEndTime,
      };
      const nextSlot = {
        ...slot,
        ...changedSlot,
        selectedIds,
      };

      if (
        slot.userId === nextSlot.userId &&
        slot.resourceId === nextSlot.resourceId &&
        slot.start === nextSlot.start &&
        slot.end === nextSlot.end &&
        slot.startTime === nextSlot.startTime &&
        slot.endTime === nextSlot.endTime &&
        slot.editedAbsenceId === nextSlot.editedAbsenceId &&
        areIdsEqual(slot.selectedIds, nextSlot.selectedIds)
      ) {
        return slot;
      }

      return nextSlot;
    });
  }, [
    absences,
    userId,
    dateFrom,
    dateTo,
    endTime,
    isDayView,
    setSelectedSlot,
    startTime,
  ]);

  const visibleRangeRef = useRef(visibleRange);
  visibleRangeRef.current = visibleRange;

  useEffect(() => {
    if (!dateFrom || !dateTo || isDayView) return;

    const range = visibleRangeRef.current;
    const fromOutside = !isWithinVisibleRange(dateFrom, range);
    const toOutside = !isWithinVisibleRange(dateTo, range);

    if (!fromOutside && !toOutside) return;

    const anchor = new Date(dateFrom);
    anchor.setDate(anchor.getDate() - SCROLL_LEAD_DAYS);
    calendar.setMonthViewFrom(anchor);
  }, [dateFrom, dateTo, calendar, isDayView]);

  useEffect(() => {
    setSelectedUserId(userId ?? null);
    if (userId) {
      // Delay scroll to run after selection sync updates
      // the calendar layout.
      const timer = setTimeout(() => {
        expandAndScrollToResource(userId);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [userId, setSelectedUserId, expandAndScrollToResource]);

  const slotStart = selectedSlotData?.start ?? null;
  const slotEnd = selectedSlotData?.end ?? null;
  const slotStartTime = selectedSlotData?.startTime ?? null;
  const slotEndTime = selectedSlotData?.endTime ?? null;
  const slotUserId = selectedSlotData?.userId ?? null;
  const slotTypeId = selectedSlotData?.typeId ?? null;
  const slotTentative = selectedSlotData?.tentative ?? false;
  const slotNotes = selectedSlotData?.notes ?? '';

  // Per-field hydration: each field reacts only to its own slot field
  // changing, so the round-trip effect's slot updates only re-hydrate the
  // field whose slot value actually changed, leaving user-cleared inputs
  // untouched.
  useEffect(() => {
    if (slotStart) setValue('dateFrom', new Date(slotStart));
  }, [slotStart, setValue]);

  useEffect(() => {
    if (slotEnd) setValue('dateTo', new Date(slotEnd));
  }, [slotEnd, setValue]);

  useEffect(() => {
    setValue('startTime', slotStartTime);
  }, [slotStartTime, setValue]);

  useEffect(() => {
    setValue('endTime', slotEndTime);
  }, [slotEndTime, setValue]);

  useEffect(() => {
    setValue('userId', slotUserId);
  }, [slotUserId, setValue]);

  useEffect(() => {
    setValue('typeId', slotTypeId);
  }, [slotTypeId, setValue]);

  useEffect(() => {
    setValue('tentative', slotTentative);
  }, [slotTentative, setValue]);

  useEffect(() => {
    setValue('notes', slotNotes);
  }, [slotNotes, setValue]);

  const onHoursToggle = (hours: boolean) => {
    if (hours !== null) {
      if (hours) {
        if (dateFrom && dateTo && dateFrom.getTime() !== dateTo.getTime()) {
          setValue('dateTo', dateFrom);
        }
        calendar.setDayView(dateFrom || new Date());
      } else {
        const date = dateFrom || new Date();

        calendar.setMonthView(date.getFullYear(), date.getMonth());
      }
    }
  };

  return (
    <>
      <UsersField disabled={disableUserField} />
      <HoursToggle
        disabled={disabled}
        hours={isDayView}
        onChange={onHoursToggle}
      />
      <DatesRangeField disabled={disabled} hours={isDayView} />
      {isDayView && <TimeRangeField disabled={disabled} />}
      <TotalDatesOrHours
        dateFrom={dateFrom}
        dateTo={dateTo}
        startTime={startTime}
        endTime={endTime}
        hours={isDayView}
        isHolidayDate={(date) =>
          holidayLookup.isHolidayDate(date, selectedUserBranchId)
        }
      />
      <TypeField disabled={disabled} />
      <NotesField disabled={disabled} />
    </>
  );
}
